import type { FastifyInstance, FastifyRequest, FastifyReply } from 'fastify'
import { BudgetService } from './budget.service.js'
import { createLogger } from '../../common/utils/logger.js'
import { authMiddleware } from '../../middleware/auth.middleware.js'
import { z } from 'zod'

const logger = createLogger('BudgetRoutes')

// Validation schemas
const createExpenseSchema = z.object({
  category: z.string().min(1),
  description: z.string().optional(),
  amount: z.number().positive(),
  currency: z.string().default('EUR'),
  date: z.string().optional().transform(val => val ? new Date(val) : new Date()),
  paidBy: z.string().min(1),
  splitWith: z.array(z.object({
    userId: z.string(),
    amount: z.number().optional(),
    percentage: z.number().optional()
  })).optional().default([])
})

const updateExpenseSchema = z.object({
  category: z.string().min(1).optional(),
  description: z.string().optional(),
  amount: z.number().positive().optional(),
  currency: z.string().optional(),
  date: z.string().optional().transform(val => val ? new Date(val) : undefined),
  paidBy: z.string().optional(),
  splitWith: z.array(z.object({
    userId: z.string(),
    amount: z.number().optional(),
    percentage: z.number().optional()
  })).optional()
})

export async function budgetRoutes(fastify: FastifyInstance) {
  const budgetService = new BudgetService()

  /**
   * GET /api/budget/:planId/expenses
   * Get all expenses for a plan
   */
  fastify.get<{
    Params: { planId: string }
  }>('/:planId/expenses', {
    preHandler: [authMiddleware]
  }, async (request, reply) => {
    const { planId } = request.params
    const userId = request.user?.uid

    if (!userId) {
      return reply.status(401).send({ error: 'Unauthorized' })
    }

    try {
      logger.info('Getting expenses for plan', { planId, userId })
      const expenses = await budgetService.getExpensesByPlanId(planId)
      return reply.send({ expenses, count: expenses.length })
    } catch (error) {
      logger.error('Failed to get expenses', { error, planId })
      return reply.status(500).send({ error: 'Failed to get expenses' })
    }
  })

  /**
   * POST /api/budget/:planId/expenses
   * Add a new expense
   */
  fastify.post<{
    Params: { planId: string }
    Body: z.infer<typeof createExpenseSchema>
  }>('/:planId/expenses', {
    preHandler: [authMiddleware]
  }, async (request, reply) => {
    const { planId } = request.params
    const userId = request.user?.uid

    if (!userId) {
      return reply.status(401).send({ error: 'Unauthorized' })
    }

    try {
      const validatedBody = createExpenseSchema.parse(request.body)
      
      logger.info('Creating expense', { planId, userId, category: validatedBody.category })
      
      const expense = await budgetService.createExpense(planId, userId, {
        category: validatedBody.category,
        description: validatedBody.description,
        amount: validatedBody.amount,
        currency: validatedBody.currency,
        date: validatedBody.date,
        paidBy: validatedBody.paidBy,
        splitWith: validatedBody.splitWith
      })

      return reply.status(201).send(expense)
    } catch (error) {
      if (error instanceof z.ZodError) {
        return reply.status(400).send({ error: 'Invalid request body', details: error.errors })
      }
      logger.error('Failed to create expense', { error, planId })
      return reply.status(500).send({ error: 'Failed to create expense' })
    }
  })

  /**
   * PATCH /api/budget/expenses/:expenseId
   * Update an expense
   */
  fastify.patch<{
    Params: { expenseId: string }
    Body: z.infer<typeof updateExpenseSchema>
  }>('/expenses/:expenseId', {
    preHandler: [authMiddleware]
  }, async (request, reply) => {
    const { expenseId } = request.params
    const userId = request.user?.uid

    if (!userId) {
      return reply.status(401).send({ error: 'Unauthorized' })
    }

    try {
      const validatedBody = updateExpenseSchema.parse(request.body)
      
      logger.info('Updating expense', { expenseId, userId })
      
      const expense = await budgetService.updateExpense(expenseId, userId, validatedBody)
      
      if (!expense) {
        return reply.status(404).send({ error: 'Expense not found' })
      }

      return reply.send(expense)
    } catch (error) {
      if (error instanceof z.ZodError) {
        return reply.status(400).send({ error: 'Invalid request body', details: error.errors })
      }
      logger.error('Failed to update expense', { error, expenseId })
      return reply.status(500).send({ error: 'Failed to update expense' })
    }
  })

  /**
   * DELETE /api/budget/expenses/:expenseId
   * Delete an expense
   */
  fastify.delete<{
    Params: { expenseId: string }
  }>('/expenses/:expenseId', {
    preHandler: [authMiddleware]
  }, async (request, reply) => {
    const { expenseId } = request.params
    const userId = request.user?.uid

    if (!userId) {
      return reply.status(401).send({ error: 'Unauthorized' })
    }

    try {
      logger.info('Deleting expense', { expenseId, userId })
      
      const deleted = await budgetService.deleteExpense(expenseId, userId)
      
      if (!deleted) {
        return reply.status(404).send({ error: 'Expense not found' })
      }

      return reply.status(204).send()
    } catch (error) {
      logger.error('Failed to delete expense', { error, expenseId })
      return reply.status(500).send({ error: 'Failed to delete expense' })
    }
  })

  /**
   * GET /api/budget/:planId/summary
   * Get expense summary for a plan
   */
  fastify.get<{
    Params: { planId: string }
  }>('/:planId/summary', {
    preHandler: [authMiddleware]
  }, async (request, reply) => {
    const { planId } = request.params
    const userId = request.user?.uid

    if (!userId) {
      return reply.status(401).send({ error: 'Unauthorized' })
    }

    try {
      logger.info('Getting expense summary', { planId, userId })
      const summary = await budgetService.getExpenseSummary(planId)
      return reply.send(summary)
    } catch (error) {
      logger.error('Failed to get expense summary', { error, planId })
      return reply.status(500).send({ error: 'Failed to get expense summary' })
    }
  })

  logger.info('Budget routes registered')
}
